import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import toastr from "toastr";
import { hasPerm } from "../utils/permission";
// import { authFetch } from "../utils/authFetch";

/* ---------- helpers ---------- */
const toast = (m, t = "success") => {
  toastr.options = { closeButton: true, progressBar: true, timeOut: 3000 };
  toastr[t](m);
};
export const API_BASE = process.env.REACT_APP_API_BASE || "https://backend.suvidhape.in";
// export const API_BASE = process.env.REACT_APP_API_BASE || "https://backend.macustore.com";
export const toUrl = (path) =>
  path ? (path.startsWith("http") ? path : `${API_BASE}${path}`) : null;

const authFetch = (url, opts = {}) =>
  fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

/* ---------- multipart helper ---------- */
export const sendForm = async (payload, method = "POST") => {
  const fd = new FormData();
  fd.append("id", String(payload.id));
  fd.append("actiontype", payload.actiontype);
  Object.entries(payload).forEach(([k, v]) => {
    if (["id", "actiontype"].includes(k)) return;
    if (v == null || (typeof v === "string" && !v.trim())) return;
    fd.append(k, v);
  });

  const res = await fetch(`${API_BASE}/api/v1/member/profileupdate`, {
    method,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: fd,
  });
  const out = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(out.detail || out.status || `HTTP ${res.status}`);
  return out;
};

/* ---------- component ---------- */
export default function Profile() {
  /* Route handling */
  const { id: idFromUrl } = useParams();           // /view_profile/:id
  const location = useLocation();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isSelf = location.pathname === "/profile"; // /profile -> logged‑in user

  const userid = isSelf ? currentUser.id : idFromUrl;

  /* state */
  const [info,        setInfo]    = useState(null);
  const [profileForm, setProfile] = useState(null);
  const [kycForm,     setKyc]     = useState(null);
  const [docsForm,    setDocs]    = useState([]);
  const [pwdForm,     setPwd]     = useState({ old: "", pwd: "", pwd2: "" });
  const [saving,      setSaving]  = useState(false);

  /* ---------- fetch once ---------- */
  useEffect(() => {
    if (!userid) return;
    (async () => {
      const r  = await authFetch(`${API_BASE}/api/v1/member/profile/${userid}`);
      const d  = await r.json();

      if (d.user) {
        d.user.profilepic = toUrl(d.user.profilepic);
        d.user.documents = (d.user.documents || []).map((doc) =>
          typeof doc === "string" ? toUrl(doc) : { ...doc, url: toUrl(doc.url) }
        );
      }

      setInfo(d);
      const u = d.user;
      setProfile({
        name:     u.name     ?? "",
        email:    u.email    ?? "",
        mobile:   u.mobile   ?? "",
        state:    u.state    ?? "",
        city:     u.city     ?? "",
        address:  u.address  ?? "",
        pincode:  u.pincode  ?? "",
        profilepic: null
      });
      setKyc({
        shopname:   u.shopname   ?? "",
        gstin:      u.gstin      ?? "",
        pancard:    u.pancard    ?? "",
        aadharcard: u.aadharcard ?? ""
      });
    })();
  }, [userid]);

  if (!info || !profileForm || !kycForm) return <div className="p-3">Loading…</div>;
  const { user } = info;

  /* ---------- generic handlers ---------- */
  const onText = (setter) => (e) =>
    setter((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onFileProfile = (e) => {
    const f = e.target.files[0];
    if (f) setProfile((p) => ({ ...p, profilepic: f }));
  };

  const onFileDocs = (tag) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocs((arr) => {
      const idx = arr.findIndex((it) => it.tag === tag);
      if (idx > -1) {
        const copy = [...arr];
        copy[idx] = { tag, file };
        return copy;
      }
      return [...arr, { tag, file }];
    });
  };

  /* ---------- submit handlers ---------- */
  const submitProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await sendForm({ id: userid, actiontype: "profile", ...profileForm });
      toast("Profile updated ✔");
      if (profileForm.profilepic) {
        setInfo((p) => ({
          ...p,
          user: { ...p.user, profilepic: URL.createObjectURL(profileForm.profilepic) },
        }));
        setProfile((f) => ({ ...f, profilepic: null }));
      }
    } catch (err) { toast(err.message, "error"); }
      finally      { setSaving(false); }
  };

  const submitKyc = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await sendForm({ id: userid, actiontype: "kyc", ...kycForm });
      toast("KYC updated ✔");
    } catch (err) { toast(err.message, "error"); }
      finally      { setSaving(false); }
  };

  const submitDocs = async (e) => {
    e.preventDefault();
    if (!docsForm.length) return toast("Select at least one document", "error");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("id", userid);
      fd.append("actiontype", "documents");
      docsForm.forEach(({ tag, file }) => {
        fd.append("files", file);
        fd.append("tags", tag);
      });

      const res = await fetch(
        `${API_BASE}/api/v1/member/profileupdate`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: fd,
        }
      );
      const out = await res.json();
      if (out.status !== "success") throw new Error(out.status);

      const newDocs = (out.documents || []).map((d) => toUrl(d.url || d));
      setInfo((p) => ({
        ...p,
        user: { ...p.user, documents: [...(p.user.documents || []), ...newDocs] },
      }));
      toast("Docs uploaded ✔");
      setDocs([]);
    } catch (err) { toast(err.message, "error"); }
      finally      { setSaving(false); }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (pwdForm.pwd !== pwdForm.pwd2) return toast("Passwords do not match", "error");
    try {
      setSaving(true);
      await sendForm({
        id: userid,
        actiontype: "password",
        oldpassword: pwdForm.old,
        password: pwdForm.pwd,
      });
      toast("Password changed ✔");
      setPwd({ old: "", pwd: "", pwd2: "" });
    } catch (err) { toast(err.message, "error"); }
      finally      { setSaving(false); }
  };
  return (
    <>
    <div className="page-wrapper">
        {/*page-content-wrapper*/}
        <div className="page-content-wrapper">
          <div className="page-content">
            {/*breadcrumb*/}
            <div className="page-breadcrumb d-none d-md-flex align-items-center mb-3">
              <div className="breadcrumb-title pe-3">User Profile</div>
              <div className="ps-3">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-0 p-0">
                    <li className="breadcrumb-item"><a href="javascript:;"><i className="bx bx-home-alt" /></a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">User Profile</li>
                  </ol>
                </nav>
              </div>
              
            </div>
            {/*end breadcrumb*/}
            <div className="user-profile-page">
              <div className="card radius-15">
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-12 col-lg-6 border-right">
                      <div className="d-md-flex align-items-center">
                        <div className="mb-md-0 mb-3">
                          <img
                            src={user.profilepic ? user.profilepic : "/assets/images/avatars/avatar-1.png"}
                            className="rounded-circle shadow"
                            width={130}
                            height={130}
                            alt={user.name}
                          />
                        </div>
                        <div className="ms-md-4 flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <h4 className="mb-0">Name : {user.name}</h4>
                          </div>
                          <p className="mb-0 text-muted"><i className="bx bx-envelope" /> {user.email}</p>
                          <p className="text-primary"><i className="bx bx-phone" /> {user.mobile}</p>
                          
                          {/* <button type="button" className="btn btn-primary">Connect</button>
                          <button type="button" className="btn btn-outline-secondary ml-2">Resume</button> */}
                        </div>
                      </div>
                    </div>
                    {user.role_id !== "1" && (
                    <div className="col-12 col-lg-6">
                      <table className="table table-sm table-borderless mt-md-0 mt-3">
                        <tbody>
                          <tr>
                            <th>Merchant Code:</th>
                            <td>{user.merchantcode}</td>
                          </tr>
                          <tr>
                            <th>Address:</th>
                            <td>{user.address}</td>
                          </tr>
                          <tr>
                            <th>Zipcode:</th>
                            <td>{user.pincode}</td>
                          </tr>
                          <tr>
                            <th>PAN:</th>
                            <td>{user.pancard}</td>
                          </tr>
                          <tr>
                            <th>Aadhar:</th>
                            <td>{user.aadharcard}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="mb-3 mb-lg-0 d-none"> 
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-github" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-twitter" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-facebook" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-linkedin" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-dribbble" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-stack-overflow" /></a>
                      </div>
                    </div>
                    )}
                    
                  </div>
                  {/*end row*/}
                  <ul className="nav nav-pills">
                    <li className="nav-item"> <a className="nav-link active" data-bs-toggle="tab" href="#Experience"><span className="p-tab-name">Profile Update</span><i className="bx bx-donate-blood font-24 d-sm-none" /></a>
                    </li>
                    {hasPerm("user_profile_kyc") && (
                    <li className="nav-item"> <a className="nav-link" id="profile-tab" data-bs-toggle="tab" href="#Biography"><span className="p-tab-name">KYC Update</span><i className="bx bxs-user-rectangle font-24 d-sm-none" /></a>
                    </li>
                    )}
                    {hasPerm("user_profile_document") && (
                    <li className="nav-item"> <a className="nav-link" data-bs-toggle="tab" href="#Edit-Profile"><span className="p-tab-name">Upload Documents</span><i className="bx bx-message-edit font-24 d-sm-none" /></a>
                    </li>
                    )}
                    {hasPerm("user_profile_password") && (
                    <li className="nav-item"> <a className="nav-link" data-bs-toggle="tab" href="#t4"><span className="p-tab-name">Password Change</span><i className="bx bx-message-edit font-24 d-sm-none" /></a>
                    </li>
                    )}
                  </ul>
                  <div className="tab-content mt-3">
                    <div className="tab-pane fade show active" id="Experience">
                      <div className="card shadow-none border mb-0 radius-15">
                        <div className="card-body">
                          <div className="card-title d-flex align-items-center">
                            <div><i className="bx bxs-user me-1 font-22 text-primary" /></div>
                            <h5 className="mb-0 text-primary">User Profile</h5>
                          </div>
                          <hr />
                          <form className="row g-3 " onSubmit={submitProfile}>
                            {["name","email","mobile","state","city","pincode"].map(f => (
                              <div className="col-md-4" key={f}>
                                <label className="form-label text-capitalize">{f}</label>
                                <input className="form-control"
                                      name={f} value={profileForm[f]} onChange={onText(setProfile)}/>
                              </div>
                            ))}
                            <div className="col-md-4">
                              <label className="form-label">Profile Pic</label>
                              <input type="file" accept="image/*" className="form-control"
                                    onChange={onFileProfile}/>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Address</label>
                              <textarea rows={2} className="form-control"
                                        name="address" value={profileForm.address}
                                        onChange={onText(setProfile)}/>
                            </div>
                            {hasPerm("user_profile_edit") && (
                            <div className="col-12 text-start">
                              <button className="btn btn-primary" disabled={saving}>Save Profile</button>
                            </div>
                            )}
                          </form>
                        </div>
                      </div>
                    </div>
                    <div className="tab-pane fade" id="Biography">
                      <div className="row">
                        <div className="card shadow-none border mb-0 radius-15">
                            <div className="card-body">
                              <div className="card-title d-flex align-items-center">
                                <div><i className="bx bxs-user me-1 font-22 text-primary" /></div>
                                <h5 className="mb-0 text-primary">KYC Details</h5>
                              </div>
                              <hr />
                              <form className="row g-3" onSubmit={submitKyc}>
                                {[
                                  ["shopname","Shop Name"],
                                  ["gstin","GST Number"],
                                  ["pancard","PAN Number"],
                                  ["aadharcard","Aadhar Number"]
                                ].map(([n,l]) => (
                                  <div className="col-md-6" key={n}>
                                    <label className="form-label">{l}</label>
                                    <input className="form-control"
                                          name={n} value={kycForm[n]} onChange={onText(setKyc)}/>
                                  </div>
                                ))}
                                <div className="col-12 text-start">
                                  <button className="btn btn-primary" disabled={saving}>Save KYC</button>
                                </div>
                              </form>
                            </div>
                        </div>
                      </div>
                    </div>
                    <div className="tab-pane fade" id="Edit-Profile">
                      <div className="card shadow-none border mb-0 radius-15">
                        <div className="card-body">
                          <div className="card-title d-flex align-items-center">
                            <div><i className="bx bxs-user me-1 font-22 text-primary" /></div>
                            <h5 className="mb-0 text-primary">Document Upload</h5>
                          </div>
                          <form className="row g-3" onSubmit={submitDocs}>
                            {[
                              ["pancardimg",      "PAN Card"],
                              ["aadhar_front", "Aadhar Front"],
                              ["aadhar_back",  "Aadhar Back"],
                              ["shop_act",     "Shop Act"],
                              // और tag‑label जोड़ना हो तो यहाँ add कर दें
                            ].map(([tag,label]) => (
                              <div className="mb-3" key={tag}>
                                <label className="form-label">{label}</label>
                                <input type="file" accept="image/*,application/pdf"
                                      className="form-control"
                                      onChange={onFileDocs(tag)}/>
                              </div>
                            ))}
                            <div className="col-12 text-start">
                            <button className="btn btn-primary" disabled={saving}>Upload Docs</button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>

                    <div className="tab-pane fade" id="t4">
                      <div className="card shadow-none border mb-0 radius-15">
                        <div className="card-body">
                          <div className="card-title d-flex align-items-center">
                            <div><i className="bx bxs-user me-1 font-22 text-primary" /></div>
                            <h5 className="mb-0 text-primary">Change Password</h5>
                          </div>
                          <form className="row g-3" onSubmit={submitPwd}>
                            {[
                              ["old","Old Password"],
                              ["pwd","New Password"],
                              ["pwd2","Repeat New"]
                            ].map(([n,l]) => (
                              <div className="col-md-4" key={n}>
                                <label className="form-label">{l}</label>
                                <input type="password" className="form-control"
                                      name={n} value={pwdForm[n]} onChange={onText(setPwd)}/>
                              </div>
                            ))}
                            <div className="col-12 text-start">
                              <button className="btn btn-primary" disabled={saving}>Change Password</button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>


                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/*end page-content-wrapper*/}
      </div>
    </>
  );
}
